/**
 * Étapes du parcours d'ÉVALUATION initiale, en cartes côte à côte (validé par Valérie le 14/10/2026).
 * Remplace, pour ce seul parcours, la liste des phases de `CertificationPathsPage` :
 * l'étape à jouer est mise en avant, les suivantes sont en retrait et disent APRÈS QUELLE
 * ÉTAPE elles s'ouvrent (le mot « Verrouillé » laissait croire à un refus). Les parcours
 * de certification gardent leur liste détaillée.
 */
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Play } from "lucide-react";
import { evalStepStates } from "../lib/eval-steps";

type Phase = {
  id: string;
  simulatorId?: string | null;
  accessible?: boolean;
  progress?: { status?: string | null; sessionsCount?: number | null } | null;
};

// Toutes les étapes sur une ligne en grand écran (5 aujourd'hui : une 5e carte seule à la ligne
// cassait la lecture « étapes qui se suivent »). Classes écrites en entier pour Tailwind.
const LG_COLUMNS: Record<number, string> = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };

export function EvalPhaseSteps({
  phases,
  simulators,
  pickTitle,
  onLaunch,
}: {
  phases: Phase[];
  simulators: Array<{ id: string; name: string }>;
  pickTitle: (o: any) => string;
  onLaunch: (phase: Phase, stepNumber: number) => void;
}) {
  const { t } = useTranslation();
  const states = evalStepStates(phases);

  return (
    <div className="space-y-3">
      <p className="text-sm" style={{ color: "#384872" }}>{t("evalPath.stepsRule")}</p>
      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${LG_COLUMNS[phases.length] ?? "lg:grid-cols-3"}`}>
        {phases.map((ph, i) => {
          const state = states[i];
          const n = i + 1;
          const simName = simulators.find((s) => s.id === ph.simulatorId)?.name;
          const title = pickTitle(ph);

          if (state === "done") {
            const played = ph.progress?.sessionsCount ?? 0;
            return (
              <div key={ph.id} className="flex flex-col gap-3 rounded-2xl border bg-white p-4" style={{ borderColor: "#B9DEC5" }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white" style={{ background: "#1E7A3C" }}>
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: "#E6F4EA", color: "#1E7A3C" }}>{t("evalPath.stepDone")}</span>
                </div>
                <div>
                  <p className="font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{title}</p>
                  {simName && <p className="text-xs text-muted-foreground">{simName}</p>}
                </div>
                {played > 0 && <p className="mt-auto text-xs text-muted-foreground">{t("evalPath.stepSessionsPlayed", { count: played })}</p>}
              </div>
            );
          }

          if (state === "upcoming") {
            return (
              <div key={ph.id} className="flex flex-col gap-3 rounded-2xl border border-dashed p-4" style={{ borderColor: "#C4C8D8", background: "#F0F1F6", color: "#5B6584" }}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-white text-sm font-bold" style={{ borderColor: "#C4C8D8", fontFamily: "Sora, sans-serif" }}>{n}</span>
                <div>
                  <p className="font-semibold" style={{ color: "#4A5578", fontFamily: "Sora, sans-serif" }}>{title}</p>
                  {simName && <p className="text-xs">{simName}</p>}
                </div>
                <p className="mt-auto flex items-center gap-1.5 text-xs font-medium" style={{ color: "#4A5578" }}>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t("evalPath.stepOpensAfter", { n: i })}
                </p>
              </div>
            );
          }

          // current (mise en avant) ou open (autre étape déjà ouverte)
          const focus = state === "current";
          return (
            <div
              key={ph.id}
              className={`flex flex-col gap-3 rounded-2xl bg-white p-4 ${focus ? "border-2 sm:-translate-y-1" : "border"}`}
              style={focus ? { borderColor: "#988EF2", boxShadow: "0 12px 28px rgba(90, 75, 190, 0.18)" } : { borderColor: "#988EF2" }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "#5A4BBE", fontFamily: "Sora, sans-serif" }}>{n}</span>
                {focus && <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: "#EEE9FB", color: "#5A4BBE" }}>{t("evalPath.stepCurrent")}</span>}
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{title}</p>
                {simName && <p className="text-xs text-muted-foreground">{simName}</p>}
              </div>
              <button
                type="button"
                onClick={() => onLaunch(ph, n)}
                className="mt-auto flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold text-white transition hover:opacity-90"
                style={{ background: "#5A4BBE" }}
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                {t("evalPath.stepStart", { n })}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
